import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
// Error codes defined in src/errors.js (IRIS-xxx)
import useTheme from './useTheme';
import LoadingDots from './components/LoadingDots';
import ThemeToggle from './components/ThemeToggle';
import Login from './components/Login';
import Admin from './components/Admin';
import Dashboard from './components/Dashboard';
import Resources from './components/Resources';

const FONT = "'DM Sans', system-ui, sans-serif";

const spinnerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg)',
};

const centeredPageStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg)',
  fontFamily: FONT,
};

const centeredCardStyle = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '48px 40px',
  width: 400,
  maxWidth: '90vw',
};

// ── Decide which screen an authenticated user should see ──────────────────────
// Returns: { screen, partnerRole? }
// screen: 'admin' | 'dashboard' | 'partner' | 'unauthorized'
async function resolveScreen(user) {
  if (!supabase) return { screen: 'unauthorized' };
  console.log('[Auth] resolveScreen — user:', user.id, user.email);

  const { data: adminRow, error: adminErr } = await supabase
    .from('admin_users')
    .select('email')
    .eq('email', user.email)
    .maybeSingle();
  console.log('[Auth] admin_users result:', { adminRow, adminErr });
  if (adminRow) return { screen: 'admin' };

  const { data: clientRows, error: clientErr } = await supabase
    .from('clients')
    .select('id')
    .contains('authorized_emails', [user.email])
    .limit(1);
  const clientRow = clientRows?.[0] ?? null;
  console.log('[Auth] clients authorized_emails result:', { clientRow, clientErr });

  if (clientRow?.id) {
    const { data: existing } = await supabase
      .from('client_users')
      .select('id')
      .eq('client_id', clientRow.id)
      .eq('user_id', user.id)
      .maybeSingle();
    if (!existing) {
      await supabase.from('client_users').insert({ client_id: clientRow.id, user_id: user.id });
    }
    return { screen: 'dashboard' };
  }

  // Check partner_users table (may not exist yet — gracefully handle)
  const { data: partnerRow } = await supabase
    .from('partner_users')
    .select('role')
    .eq('email', user.email)
    .maybeSingle();
  if (partnerRow) return { screen: 'partner', partnerRole: partnerRow.role };

  console.log('[Auth] no match — unauthorized');
  return { screen: 'unauthorized' };
}

// ── Root component — pure state machine, no router ────────────────────────────
// status: 'loading' | 'login' | 'admin' | 'dashboard' | 'unauthorized'
export default function App() {
  const [status, setStatus] = useState('loading');
  const [user, setUser] = useState(null);
  const [partnerRole, setPartnerRole] = useState(null);
  const resolving = useRef(false);
  const { toggle: toggleTheme, isDark } = useTheme();

  useEffect(() => {
    if (!supabase) return undefined;
    let cancelled = false;

    async function handleUser(u, source) {
      if (resolving.current) {
        console.log(`[Auth] handleUser (${source}) skipped — already resolving`);
        return;
      }
      resolving.current = true;
      console.log(`[Auth] handleUser — source: ${source}, user: ${u.id}`);

      setStatus('loading');
      setUser(u);

      const result = await Promise.race([
        resolveScreen(u),
        new Promise(resolve => setTimeout(() => {
          console.log('[Auth] resolveScreen timed out — defaulting to unauthorized');
          resolve({ screen: 'unauthorized' });
        }, 5000)),
      ]);

      if (!cancelled) {
        console.log(`[Auth] → setting status: ${result.screen}`);
        if (result.partnerRole) setPartnerRole(result.partnerRole);
        setStatus(result.screen);
      }
      resolving.current = false;
    }

    console.log('[Auth] calling getSession()');
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      console.log('[Auth] getSession result:', {
        user: session?.user?.id ?? null,
        error: error?.message ?? null,
      });
      if (cancelled) return;
      if (session?.user) {
        handleUser(session.user, 'getSession');
      } else {
        console.log('[Auth] no session — showing login');
        setStatus('login');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[Auth] onAuthStateChange:', event, session?.user?.id ?? 'no user');
        if (cancelled) return;

        if (event === 'SIGNED_IN' && session?.user) {
          resolving.current = false;
          handleUser(session.user, 'SIGNED_IN');
        } else if (event === 'SIGNED_OUT') {
          resolving.current = false;
          setUser(null);
          setStatus('login');
        }
      }
    );

    const safety = setTimeout(() => {
      setStatus(prev => {
        if (prev === 'loading') {
          console.log('[Auth] safety timeout — forcing login');
          return 'login';
        }
        return prev;
      });
    }, 4000);

    return () => {
      cancelled = true;
      clearTimeout(safety);
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSignOut() {
    if (supabase) await supabase.auth.signOut();
    window.location.href = 'https://irisaeo.com';
  }

  console.log('[App] render — status:', status, '| user:', user?.id ?? null);

  if (!supabase) {
    return (
      <div style={spinnerStyle}>
        <div style={centeredCardStyle}>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 10px 0', color: 'var(--text)' }}>Configuration needed</h1>
          <p style={{ fontSize: 13, color: 'var(--accent)', fontFamily: 'monospace', margin: '0 0 8px 0' }}>IRIS-400</p>
          <p style={{ fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.6, margin: 0 }}>
            This deployment is missing <code style={{ fontSize: 13, color: 'var(--accent)' }}>REACT_APP_SUPABASE_URL</code> and{' '}
            <code style={{ fontSize: 13, color: 'var(--accent)' }}>REACT_APP_SUPABASE_ANON_KEY</code> in Vercel → Project → Settings → Environment Variables.
            Add them for <strong>Production</strong>, then redeploy.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'loading')      return <Spinner />;
  if (status === 'login')        return <Login isDark={isDark} onToggleTheme={toggleTheme} />;
  if (status === 'unauthorized') return <Unauthorized onSignOut={handleSignOut} isDark={isDark} onToggleTheme={toggleTheme} />;
  if (status === 'admin')        return <Admin onSignOut={handleSignOut} isDark={isDark} onToggleTheme={toggleTheme} />;
  if (status === 'dashboard')    return <Dashboard user={user} onSignOut={handleSignOut} isDark={isDark} onToggleTheme={toggleTheme} />;
  if (status === 'partner')      return <Resources user={user} role={partnerRole || 'viewer'} onSignOut={handleSignOut} isDark={isDark} onToggleTheme={toggleTheme} />;
  return null;
}

function Spinner() {
  return (
    <div style={spinnerStyle}>
      <LoadingDots />
    </div>
  );
}

function Unauthorized({ onSignOut, isDark, onToggleTheme }) {
  return (
    <div style={centeredPageStyle}>
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <ThemeToggle isDark={isDark} onToggle={onToggleTheme} />
      </div>
      <div style={centeredCardStyle}>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px 0', color: 'var(--text)', fontFamily: "'Syne', system-ui, sans-serif" }}>Access not set up yet</h1>
        <p style={{ fontSize: 13, color: 'var(--accent)', fontFamily: 'monospace', margin: '0 0 8px 0' }}>IRIS-101</p>
        <p style={{ fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.6, margin: '0 0 20px 0' }}>
          Your email is not authorized to access this portal.
          Contact Iris Digital Consulting to get access.
        </p>
        <p style={{ fontSize: 14, color: 'var(--text-dim)', margin: '0 0 24px 0' }}>
          <a href="mailto:samwudel.iris@gmail.com" style={{ color: 'var(--accent)', fontWeight: 500 }}>
            samwudel.iris@gmail.com
          </a>
        </p>
        <button onClick={onSignOut} style={{ padding: '9px 18px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: FONT }}>
          Sign out
        </button>
      </div>
    </div>
  );
}
